import get from 'lodash/get'
import { autorun, flow, computed, isObservableMap, makeObservable, observable, action } from 'mobx'
import { PromisedComputed, promisedComputed } from '../../utils/computed-async-mobx'
import { fnFromCode } from '../../utils/fn'
import toJS from '../../utils/tojs'
import { uistore } from '../uistore'
import { createQueryString, IDataTransformer } from './filter'
import { JSProvider } from './javascript'
import type { IMapping } from './mapping'
import { isField } from './../../utils/type-utils'
import type { IField } from './field'

let jsProvider
const UNBOUND_PARAM = Symbol('Unbound')
const stepsCache = new WeakMap()

const pausedFn = () => uistore?.propsMapperPaused

export default class Mapper {
  error?: {message: string} = null
  errType: 'error'|'warning' = 'error'
  pollId = null
  promised: PromisedComputed<Mapper>
  mapping: IMapping
  overrides: Record<string, Mapper>

  constructor (mapping: IMapping, overrides?: Record<string, Mapper>, key?: string) {
    if(process.env.NODE_ENV === 'development' && !key) {
      key = mapping.fullKey
    }
    makeObservable(this, {
      error: observable,
      setError: action,
      resolvedChildren: computed,
      ds: computed,
      boundFunction: computed,
      busy: computed,
      toProps: computed,
      whenReady: flow.bound
    }, { name: key })
    this.promised = promisedComputed(mapping.ssrProps, this.resolve.bind(this), { pausedFn, name: key && ('async:' + key) })
    this.mapping = mapping
    if (overrides && typeof overrides === 'object') {
      this.overrides = overrides
    }
  }

  poll() {
    if(this.pollId) clearTimeout(this.pollId)
    if(!this.mapping?.intervalMs) return
    this.promised.refreshCallCount
    const interval = this.mapping.intervalMs < 1000 ? 1000 : this.mapping.intervalMs
    this.pollId = setTimeout(() => {
      if(document.hidden || (this.promised.pr && this.promised.pr.state !== 'fulfilled')) {
        this.poll()
      } else {
        this.promised.refresh()
      }
    }, interval)
  }
  
  setError = (e?: { message: string }, errType: 'error'|'warning' = 'error') => {
    this.error = e
    this.errType = errType
  }
  
  resolveAsync = () => this.whenReady(true)

  convertProps = this.resolveAsync
  
  getMapper (mapping) {
    if (this.overrides && mapping && !mapping.mapper?.overrides) {
      const mapper = new Mapper(mapping, this.overrides)
      return mapper
    }
    return mapping?.mapper
  }

  shouldResolveChildren () {
    const { children, bindingType } = this.mapping
    return children && !['function', 'multi'].includes(bindingType)
  }

  get resolvedChildren () {
    uistore?.lastRefreshTime
    if (!this.shouldResolveChildren()) return

    const resolved = {}
    for (const c of this.mapping.children) {
      const mapper = this.getMapper(c)
      resolved[c.key] = mapper?.toProps
    }
    return resolved
  }

  resolveData () {
    uistore?.lastRefreshTime
    const data = this.resolveBinding()

    if (this.shouldRunFn(data)) {
      return this.runFn(data)
    }

    return data
  }

  resolve () {
    uistore?.lastRefreshTime
    const children = this.resolvedChildren
    const data = this.resolveData()
    if(data?.then) return data.then(action(data => convertProps.call(this.mapping, data, children, this.setError)))
    return convertProps.call(this.mapping, data, children, this.setError)
  }

  get ds () {
    const { dataSource } = this.mapping
    if (!dataSource) return null
    if (this.overrides?.[dataSource.id]) return this.overrides[dataSource.id]
    return uistore?.dataSourceFactory?.getInstance(dataSource.id)
  }

  get boundFunction () {
    const { dataSource, value = {}, js, children, componentMapping } = this.mapping
    let f
    if (js && typeof value === 'string') {
      if (!jsProvider) jsProvider = new JSProvider()
      const inputs = children || []
      const code = fnFromCode(value, inputs)
      f = (...params) => jsProvider.runCode(code, inputs, params, this.mapping)
    } else if (dataSource && this.ds) {
      if (this.ds.config.type === 'JavaScript' || this.ds.config.type === 'Function') {
        this.ds.config.code
        this.ds.config.mapping?.toProps
        f = (...params) => this.ds.run(...params)
      } else if (isField(value)) {
        f = get(this.ds, value.fieldPath)
      }
      //      if (typeof f === 'function') f = f.bind(this.ds)
    } else if (componentMapping) {
      f = this.resolveComponentMapping()
    }
    return f
  }

  boundFunctionParams (computed = true) {
    const { children } = this.mapping
    const params = []
    for (const c of children || []) {
      let param
      const mapper = this.getMapper(c)
      const otherMapper = this.getMapper(c.mappedToMapping)
      if (c.bindingType && mapper) {
        if (c.mappedToMapping && otherMapper) {
          param = computed ? otherMapper.toProps : stepsCache.get(c.mappedToMapping)
        } else {
          param = computed ? mapper.toProps : mapper.resolveAsync()
        }
      } else if (!c.children || !c.children.length) {
        param = UNBOUND_PARAM
      }
      params.push(param)
    }
    return params
  }

  binding_function_defer () {
    const f = this.boundFunction
    if (typeof f !== 'function') return

    const fn = async (...p) => {
      let args = []
      const params = this.boundFunctionParams(false)
      if (!params.length) args = p
      else {
        for (const param of params) {
          if (param === UNBOUND_PARAM) args.push(p.shift())
          else {
            args.push(param?.then ? await param : param)
          }
        }
      }

      return f(...args)
    }
    return fn
  }

  binding_function (computed = true) {
    const f = this.boundFunction
    const { children } = this.mapping

    if (typeof f !== 'function') return

    const params = this.boundFunctionParams(computed)
    
    const fn = async (...p) => {
      let args = []
      
      if(computed) {
        let b = false
        if (children && children.length) {
          for (const c of children) {
            const mapper = this.getMapper(c)
            if(mapper?.busy) b = true
          }
        }
  
        if (b) {
          return this.cached
        }
      }

      if (!params.length) args = p
      else {
        for (const param of params) {
          if (param === UNBOUND_PARAM) args.push(p.shift())
          else args.push(param?.then ? await param : param)
        }
      }
      
      return f(...args)
    }

    return fn
  }

  resolveComponentMapping () {
    const { componentMapping, value } = this.mapping
    const fieldPath = (value as IField).fieldPath;
    let path: string, data;
    if (this.overrides?.[componentMapping.id]) {
      data = this.overrides[componentMapping.id].toProps
      path = fieldPath.replace(/^props\./, '')
      return get(data, path)
    } else if (fieldPath.indexOf('state') === 0 && componentMapping.state) {
      data = componentMapping.state
      path = fieldPath.replace(/^state\./, '')
      return get(data, path)
    } else {
      path = fieldPath.replace(/^props\./, '')
      let key = path?.match(/^[A-Za-z_$0-9]+/)?.[0]
      if(key) {
        const mapper = this.getMapper(componentMapping.mapping?.asObject?.[key])
        if(!mapper) {
          return componentMapping.renderProps?.[key]
        } else if(mapper === this) {
          data = this.cached
          return get(data, path)
        } else {
          mapper?.toProps
          return mapper?.resolveAsync().then(props => {
            data = { [key]: props || componentMapping.renderProps?.[key] }
            return get(data, path)
          })
        }
      }      
    }
  }

  binding_data () {
    const { mappedToMapping, componentMapping, dataSource, value = {} } = this.mapping
    if (dataSource && this.ds && this.mapping.parentDataSource?.id !== dataSource.id) {
      this.ds.data && this.ds.data.length && this.ds.data[0]
      const pending = uistore?.dataSourceFactory.pending?.[dataSource.id]
      const f = () => isField(value) ? get(this.ds.data, value.fieldPath) : this.ds.data
      return pending ? () => pending.then(f) : f()
    }
    if (!dataSource && componentMapping && isField(value)) {
      return this.resolveComponentMapping()
    }

    if (!dataSource && mappedToMapping) {
      return stepsCache.get(mappedToMapping)
    }
  }

  binding_value (computed = true) {
    const { type, value } = this.mapping
    toJS(value)
    if (type === 'Transformation') {
      if (computed) {
        return {
          fields: value && toJS((value as IDataTransformer).queryFields),
          query: value && (value as IDataTransformer).qString
        }
      } else {
        return async () => ({
          fields: value && toJS((value as IDataTransformer).queryFields),
          query: value && (await createQueryString(value))
        })
      }
    }

    return value
  }

  binding_multi () {
    const { children } = this.mapping
    if (!children) return
    const params = []

    for (const c of children) {
      const mapper: Mapper = this.getMapper(c)
      if (mapper.busy) return
      params.push([c, mapper.toProps])
    }

    return async (...p) => {
      let lastResult
      for (const [step, val] of params) {
        if (typeof val === 'function') lastResult = await val(...p)
        else lastResult = val
        stepsCache.set(step, lastResult)
      }
      return lastResult
    }
  }

  binding_multi_defer () {
    const { children } = this.mapping
    if (!children) return

    return async (...p) => {
      let lastResult
      for (const step of children) {
        const mapper = this.getMapper(step)
        const f = await mapper.resolveAsync()
        if (typeof f === 'function') lastResult = await f(...p)
        else lastResult = f
        stepsCache.set(step, lastResult)
      }
      return lastResult
    }
  }

  resolveBinding (computed = true) {
    const { value, bindingType, type } = this.mapping
    if (this.ds) {
      if (this.mapping.isBoundToRef) {
        const fieldPath = (value as IField).fieldPath;

        if (fieldPath === '$') return this.ds
        return get(this.ds, fieldPath.replace(/^\$\./, ''))
      }
    }
    if (bindingType) {
      let fn = this['binding_' + bindingType]
      if (type === 'func') {
        if (bindingType === 'function') fn = this.binding_function_defer
        else if (bindingType === 'multi') fn = this.binding_multi_defer
      }
      return fn && fn.call(this, computed)
    }
  }

  async runFn (fn) {
    try {
      const res = await (fn.then ? fn : fn())
      this.setError()
      return res
    } catch (e) {
      this.setError(e)
    }
  }

  shouldRunFn (fn) {
    if (typeof fn === 'function' && this.mapping.type !== 'func' || fn?.then) {
      return true
    }
    return false
  }

  whenReady = function * whenReady(this: Mapper, keepAlive = true) {
    let disposer
    if(keepAlive) disposer = autorun(() => this.toProps)
    for(let c of this.mapping.children || []) {
      let m = this.getMapper(c)
      if(m) yield m.whenReady(false)
    }
    
    if (!this.mapping.dataSource && this.mapping.componentMapping && isField(this.mapping.value)) {
      yield this.resolveComponentMapping()
    }

    const value = ((this.mapping.value as unknown) as Mapper);
    if(value?.whenReady) {
      yield value.whenReady();
    }

    yield this.promised.currentState
    
    disposer?.()
    return this.toProps
  }

  get busy () {
    if (uistore?.propsMapperPaused) return true

    if (this.mapping.type === 'Transformation' && this.mapping.value) {
      return (this.mapping.value as IDataTransformer).busy
    }
    let b = false
    for(let c of this.mapping.children || []) {
      if(c.mapper?.busy) b = true
    }
    return this.promised.busy || b
  }

  get toProps () {
    this.poll()
    return this.promised.value
  }

  get cached (): Mapper|undefined {
    return (this.promised.cached as any)?.value
  }
}

function checkType(checkType, val) {
  let err
  if (val === undefined) {
    // checking against undefined here is interfering with error checks elsewhere
    return null
  } else if (checkType === 'string' && Array.isArray(val)) {
    err = `Expected a string value, got 'list'`
  } else if (checkType === 'string' && (typeof val === 'object' && val !== null)) {
    err = `Expected a string value, got 'object'`
  } else if (checkType === 'list' && !Array.isArray(val)) {
    err = `Expected a list value, got '${typeof val}'`
  } else if (checkType === 'number' && typeof val !== 'number') {
    err = `Expected a number value, got '${typeof val}'`
  } else if (checkType === 'object' && typeof val !== 'object') {
    err = `Expected an object value, got '${typeof val}'`
  } else if (checkType === 'list' || checkType === 'number' || checkType === 'object' || checkType === 'string') {
    err = ''
  }
  return err
}

function convertProps (this: Mapper['mapping'], data, childrenProps = {}, setError: Mapper['setError']) {
  let propVal

  if (!this) return

  if (setError && this.bindingType) {
    let err = checkType(this.type, data)
    const expectingMissingValue = (this.bindingType === 'value' && this.children === null) || (this.bindingType === 'data' && !this.dataSource)
    if (this.bindingType === 'data' && !!this.value && data === undefined) {
      err = 'Bound to an undefined value'
    }
    if (err === '' || expectingMissingValue) {
      setError(null)
    } else if (err) {
      setError({ message: err }, 'warning')
    }
  }

  if (this.bindingType === 'null' || (data === null && this.bindingType === 'function')) return null

  const t = this.type
  switch (t) {
    case 'multiselect':
      if (data) {
        propVal = typeof data === 'string' ? [data] : data
      } else {
        if (Array.isArray(this.value) && this.value.length) {
          if ((this.value[0] as any).value) {
            propVal = this.value.map(v => v.value)
          } else {
            propVal = this.value
          }
        } else {
          propVal = []
        }
      }
      break
    case 'slider':
      if (Array.isArray(this.value) && this.value.length && this.value.length === 4) {
        if (this.bindingType === 'value') {
          propVal = this.value[3]
        } else {
          propVal = null
        }
      }
      break
    case 'Transformation':
      propVal = data
      break
    case 'object':
      propVal = parseObjVal(this, data, childrenProps)
      break
    case 'Records':
      propVal = data && Array.isArray(data) && data.map(d => parseRecordVal(this, d))
      break
    case 'Record':
      propVal = parseRecordVal(this, data, childrenProps)
      break
    case 'list':
      propVal = parseListVal(this, data, childrenProps)
      break
    case 'FieldSet':
      propVal = parseFieldSet(parseListVal(this, data, childrenProps), this)
      break
    case 'PrimitiveArray':
      propVal = parsePrimitiveArray(this, data)
      break
    default:
      propVal = parsePrimitives(this, data)
      break
  }

  return propVal === null ? undefined : propVal
}

function parseFieldSet (value, mapping) {
  if (!Array.isArray(value)) return value
  value = value.filter(Boolean)
  for (const f of value) {
    if (mapping.dataSourceId && /(REFERENCE|PICKLIST)/.test(f.type)) {
      const ds = uistore?.dataSourceFactory?.getInstance(mapping.dataSourceId)
      if (!ds || !ds.config || !ds.config.fields) continue
      const field = ds.config.fields.find(ff => f.fieldPath === ff.fieldPath)
      if (field) {
        f.referenceData = field.referenceData
        f.picklist = field.picklist
      }
    }
  }
  return value
}

function parsePrimitiveArray (mapping, data) {
  let propVal
  if (data?.length) {
    if (data.filter && typeof data[0] !== 'object') {
      propVal = data.filter(x => x)
    } else if (mapping.value && mapping.value.fieldPath) {
      propVal = data && data.map(item => item[mapping.value.aliasedFieldPath])
    } else {
      propVal = data
    }
  }
  return propVal
}

function coerceToPrimitive (obj) {
  if (!obj) return obj
  if (Array.isArray(obj) && obj.length) {
    return coerceToPrimitive(obj[0])
  }
  if (typeof obj === 'object') {
    return coerceToPrimitive(obj[Object.keys(obj)[0]])
  }
  return obj
}

function parsePrimitives (mapping, data, multiType?: string) {
  let propVal

  if (data != null) {
    // if a parent gave us data and/or if bound directly to a datasource
    if (
      !mapping.componentMapping &&
      mapping.value &&
      mapping.value.fieldPath &&
      mapping.value.fieldPath.length > 0
    ) {
      // bound to a datasource field
      if (Array.isArray(data) && data.length) {
        propVal = get(data[0], mapping.value.aliasedFieldPath)
      } else if (typeof data === 'object') {
        if (isObservableMap(data)) {
          propVal = data.get(mapping.value.aliasedFieldPath)
        } else propVal = get(data, mapping.value.aliasedFieldPath)
      } else {
        propVal = data
      }
    } else {
      // bound directly to a data source, no field mapping map the first
      // primitive self.value
      if (mapping.bindingType) propVal = coerceToPrimitive(data)
    }
  } else {
    if (mapping.value && mapping.value.groupBy) {
      propVal = mapping.value.fieldPath
    } else if (
      (mapping.type === 'number' || multiType === 'number') &&
      typeof mapping.value === 'string'
    ) {
      propVal = parseFloat(mapping.value)
    } else {
      if (mapping.bindingType === 'value') {
        propVal = mapping.value
      }
    }
  }

  let val = coerceToPrimitive(propVal)
  if (mapping.type === 'color' && val && val.length) {
    if (val.trimLeft().startsWith('var(')) return val
    val = convertColor(val)
  }
  return val
}

function convertColor (val) {
  if (typeof window !== 'undefined' && window.getComputedStyle && !window.__polystack_data__?.isStaticSite) {
    let el: HTMLDivElement = document.querySelector('#convert-color')
    if (!el) {
      el = document.createElement('div')
      el.id = 'convert-color'
      el.style.display = 'none'
      document.body.appendChild(el)
    }
    el.style.color = val
    val = getComputedStyle(el).color
  }
  return val
}

function parseListVal (mapping, data, childrenProps = {}) {
  if (data && Array.isArray(data)) return data.filter(x => x)

  if (!mapping.children) return

  if (!data) {
    return Object.values(childrenProps).filter(v => v !== undefined)
  }
}

function parseObjVal (mapping, data, childrenProps = {}) {
  let propVal = {}
  let d
  if (!data && !Object.keys(childrenProps)) return
  if (data && Array.isArray(data)) d = data[0]
  else if (data && typeof data === 'object') d = data

  // super edgy case to allow binding datasource to State as a reference
  if (d && d.constructor && d.constructor.name === 'DataStoreProvider') {
    return d
  }

  if (typeof d === 'object') propVal = { ...d }
  for (const p in childrenProps) {
    const v = childrenProps[p]
    if (v !== undefined) propVal[p] = v
  }

  if (!Object.keys(propVal).length && mapping.type === 'object') return
  return propVal
}

function parseRecordVal (mapping, data, childrenProps = {}) {
  if (!mapping.dataSource && !mapping.value && !mapping.recordType) {
    return parseObjVal(mapping, data, childrenProps)
  }
  // observe the entire object, as we don't know what user JS functions might care about
  toJS(data)
  const Record = mapping.ItemClass
  let propVal = new Record({}, true)
  if (data) {
    if (Array.isArray(data)) {
      propVal = data.length && data[0]
    } else {
      propVal = data
    }
  }

  if (propVal && propVal.$ && !propVal.$.Record) propVal = new Record(propVal, true)

  for (const p in childrenProps) {
    const v = childrenProps[p]
    if (v !== undefined) propVal[p] = v
  }

  return propVal
}
